import moment, { type Moment } from "moment";
import { formatDateTime } from "../../../../../Utilities/Generic/Strings";

function coalesce<T extends Array<any>>(...args: T): T[number] {
    for(let i = 0; i < args.length; i++) {
        if(args[i] !== null && typeof args[i] !== "undefined") {
            return args[i];
        }
    }

    return null;
}

function concat(...args: Array<any>): string {
    return args.join("");
}

function format(value: string | Moment, format?: string): string {
    if(typeof format !== "string") {
        if(moment.isMoment(value)) {
            return formatDateTime(value);
        }

        return value;
    }

    let dateTime: Moment;

    if(moment.isMoment(value)) {
        dateTime = value;
    } else {
        dateTime = moment(value);
    }

    if(format && dateTime.isValid()) {
        return moment(dateTime).format(format);
    }

    if(moment.isMoment(value)) {
        return formatDateTime(value);
    }

    return value;
}

function numberFormat(
    value: string,
    prepend = "",
    append = "",
    decimalPlaces = 2,
    comma = ",",
    decimalSeparator = "."
): string {
    let parsed = parseFloat(value);

    if(isNaN(parsed)) {
        return value;
    }

    let negative = "";

    if(parsed < 0) {
        negative = "-";
        parsed = Math.abs(parsed);
    }

    let decimals = "";

    if(decimalPlaces > 0) {
        const decimalRounding = Math.pow(10, decimalPlaces);
        const roundedDecimals = Math.round(parsed % 1 * decimalRounding) / decimalRounding;

        decimals = roundedDecimals.toFixed(decimalPlaces).substring(2);
    }

    parsed = Math.floor(parsed);

    let results = parsed.toString().replace(/\d(?=(?:\d{3})+$)/g, match => `${match}${comma}`);

    if(decimals !== "") {
        results += decimalSeparator + decimals;
    }

    return negative + prepend + results + append;
}

function contains(haystack: string, needle: string): boolean {
    return haystack.toLowerCase().includes(needle.toLowerCase());
}

function equal(a: any, b: any): boolean {
    return a == b;
}

function ifFunc<If, Else>(expression: any, ifValue: If, elseValue: Else): If | Else {
    if(expression) {
        return ifValue;
    } else {
        return elseValue;
    }
}

function add(...args: Array<string | number>): number {
    let results = 0;

    for(let i = 0; i < args.length; i++) {
        let num = args[i];

        if(typeof num === "string") {
            num = parseFloat(num);
        }

        if(isNaN(num)) {
            continue;
        }

        results += num;
    }

    return results;
}

function html(content: string): string {
    /* To use when Chrome supports it
    if(typeof Sanitizer?.prototype.sanitizeFor === "function") {
        return new Sanitizer().sanitizeFor("div", content)?.innerHTML ?? "";
    }
    */

    return content.replaceAll("&lt;", "<").replaceAll("&gt;", ">");
}

export const REPLACE_FUNCTIONS = {
    "coalesce"     : coalesce,
    "format"       : format,
    "if"           : ifFunc,
    "add"          : add,
    "concat"       : concat,
    "number_format": numberFormat,
    "contains"     : contains,
    "equal"        : equal,
    "html"         : html,
};

type ReplaceFunctionKey = keyof typeof REPLACE_FUNCTIONS;

function isReplaceFunctionKey(data: string): data is ReplaceFunctionKey {
    return data in REPLACE_FUNCTIONS;
}

const IGNORE_PARSING_ARGS: Array<ReplaceFunctionKey> = [ "html" ];

/**
 * Gets the arguments from a function
 * NOTE: They must be wrapped in quotes
 * @param string
 * @returns {Array}
 */
export function getArguments(string: string): Array<string> {
    const results = [];

    let current = "";

    let quotes = 0;
    let apostrophes = 0;

    let brackets = 0;

    for(let i = 0, l = string.length; i < l; i++) {
        const char = string[i];

        if(char === "," && brackets === 0 && quotes % 2 === 0 && apostrophes % 2 === 0) {
            results.push(current.trim());
            current = "";
        } else {
            if(char === "(" || char === "<") {
                brackets++;
                // continue;
            } else if(char === ")" || char === ">") {
                brackets--;
                // continue;
            } else if(char === "\"") {
                quotes++;
            } else if(char === "'") {
                apostrophes++;
            }

            current += char;
        }
    }

    if(current !== "") {
        results.push(current.trim());
    }

    return results;
}

const NO_FUNCTION = Symbol("NO_FUNCTION");

export class TextReplacement {
    protected cache = new Map<string, unknown>();

    constructor(
        protected data: Record<string, unknown>
    ) {}

    /**
     * Get the data value at the end of the path
     * @param originalPath
     * @param original
     * @param func
     */
    protected getReplaceValue(
        originalPath: Array<string>,
        original: string,
        func?: ReplaceFunctionKey
    ): unknown {
        const path = [ ...originalPath ];

        let key = path.shift();
        let layer: unknown = this.data;

        while(typeof key !== "undefined") {
            if(typeof layer !== "object") {
                return null;
            }

            if(layer === null) {
                return null;
            }

            if(!(key in layer)) {
                return null;
            }

            // Prepare the next layer
            layer = (layer as Record<string, unknown>)[key];

            if(!path.length) {
                if(moment.isMoment(layer)) {
                    if(func !== "format") {
                        return formatDateTime(layer);
                    }

                    // Check if the value is printable
                } else if(typeof layer === "object" && layer !== null) {
                    // Value isn't printable, return original
                    return original;
                }

                return layer;
            }

            key = path.shift();
        }

        return null;
    }

    protected parseReplaceFunction(
        expression: string
    ): unknown {
        const nameRegex = /^[^(]*/g;
        const nameResults = nameRegex.exec(expression);

        if(!nameResults) {
            return NO_FUNCTION;
        }

        const name = nameResults[0].toLowerCase();

        if(!isReplaceFunctionKey(name)) {
            return NO_FUNCTION;
        }

        const argString = expression.slice(nameRegex.lastIndex + 1, -1);

        if(!argString) {
            return NO_FUNCTION;
        }

        const args = getArguments(argString);
        let parsedArgs: Array<unknown> = [];

        if(!IGNORE_PARSING_ARGS.includes(name)) {
            for(let i = 0, l = args.length; i < l; i++) {
                const parsed = parseFloat(args[i] as string);

                if(isNaN(parsed)) {
                    parsedArgs[i] = this.replace(args[i] as string, name);
                } else {
                    parsedArgs[i] = parsed;
                }
            }
        } else {
            parsedArgs = args;
        }

        try {
            const func = REPLACE_FUNCTIONS[name] as (...args: Array<unknown>) => unknown;

            return func(...parsedArgs);
        } catch(err) {
            return expression;
        }
    }

    /**
     *
     * @param expression
     * @param func
     * @protected
     */
    protected replace(
        expression: string,
        func?: ReplaceFunctionKey
    ): unknown {
        const found = this.cache.get(expression);

        if(typeof found !== "undefined") {
            return found;
        }

        let results: unknown;

        if(
            (expression.startsWith("\"") && expression.endsWith("\"")) ||
            (expression.startsWith("'") && expression.endsWith("'"))
        ) {
            // Remove the quotation marks
            results = expression.slice(1, -1);
        } else {
            // Check to see if this is a function that needs to be parsed
            const functionResults = this.parseReplaceFunction(expression);

            if(functionResults !== NO_FUNCTION) {
                return functionResults;
            }

            const path = expression.split(".");

            const replacement = this.getReplaceValue(path, expression, func);

            if(replacement === null) {
                results = "";
            } else {
                results = replacement;
            }
        }

        this.cache.set(expression, results);

        return results;
    }

    /**
     * Parse the text with the current text replacement context
     * @param text
     */
    public parse(text: string): string {
        const regex = /{.*?}/g;

        let match = regex.exec(text);

        if(!match) {
            return text;
        }

        let results = "";
        let lastIndex = 0;

        while(match) {
            const expression = match[0].slice(1, -1);

            const value = this.replace(expression);

            if(value !== null) {
                results += text.slice(lastIndex, match.index);
                results += value;

                lastIndex = regex.lastIndex;
            }

            match = regex.exec(text);
        }

        // Add remaining text
        results += text.slice(lastIndex);

        return results;
    }

    public static parse(data: Record<string, unknown>, text: string): string {
        const instance = new TextReplacement(data);

        return instance.parse(text);
    }
}
